import {Component, computed, effect, OnInit, signal} from '@angular/core';
import {AppointmentRibbonService} from "@app/areas/appointments/services/appointment-ribbon.service";

import {AppointmentParticipant} from "@app/areas/appointments/models/appointment-participant.interface";
import {AppAuthService} from "@services/core/auth/app-auth.service";
import {LocalizedDatePipe} from "@shared/pipes/localized-date/localized-date.pipe";
import {IconPath} from "@shared/common/icons";
import {IconComponent} from "@shared/components/icon/icon.component";
import {AppointmentDetail} from "@app/areas/appointments/models/appointment.interface";
import { ModalService } from '@src/app/shared/services/helpers/modal/modal.service';
import { AppointmentType } from '../../models/appointment-type.enum';
import {TooltipDirective} from "ngx-bootstrap/tooltip";
import {take} from "rxjs/operators";
import {hasValue} from "@shared/utils/helpers";
import {LocalizeService} from "@services/helpers/localize/localize.service";

@Component({
  selector: 'app-appointment-ribbon',
  imports: [
    LocalizedDatePipe,
    IconComponent,
    TooltipDirective
],
  templateUrl: './appointment-ribbon.component.html',
  styleUrl: './appointment-ribbon.component.scss',
  standalone: true
})
export class AppointmentRibbonComponent implements OnInit {
  hasAppointments = computed(() => this.service.appts$().length > 0);
  appointmentCount = computed(() => this.service.appts$().length);
  authUserId = null;

  isMinimised$ = signal<boolean>(true)
  modalIsOpen$ = false;

  AppointmentType = AppointmentType

  constructor(
    public modalService: ModalService,
    public service: AppointmentRibbonService,
    private auth: AppAuthService,
    private l: LocalizeService,
  ) {
    effect(() => {
      const u = this.auth.user$();
      if (hasValue(u) && hasValue(u?.identityProfile)) {
        this.authUserId = u.identityProfile.id;
      } else {
        this.authUserId = null;
      }
    })

  }

  ngOnInit(): void {
    this.service.refresh()
  }

  toggleMinimised(): void {
    this.isMinimised$.update(value => !value);
  }

  getToggleLabel(): string {
    return this.isMinimised$()
      ? this.l.l('Appointments.Ribbon.Show', 'Show appointments')
      : this.l.l('Appointments.Ribbon.Hide', 'Hide appointments');
  }

  async joinAppointment(appt: AppointmentDetail): Promise<void> {
    if (this.modalIsOpen$) {
      return
    }

    this.modalIsOpen$ = true;

    try {
      const {AppointmentCallModalComponent} = await import('@app/areas/appointments/components/appointment-call-modal/appointment-call-modal.component');
      this.modalService.open(AppointmentCallModalComponent, { appointmentId: appt.appointment.id })
    } catch (err) {
      this.modalIsOpen$ = false;
      throw err;
    }

    this.modalService.onClose
      .pipe(take(1))
      .subscribe(_ => this.modalIsOpen$ = false)
  }

  getAppointmentTypeIcon(apptType: AppointmentType): IconPath {
    switch (apptType) {
      case AppointmentType.Audio:
        return 'mdi-phone'
      case AppointmentType.InPerson:
        return 'mdi-account-outline'
      case AppointmentType.Video:
        return 'mdi-video'
      default:
        return null;
    }
  }

  getInitials(p: AppointmentParticipant): string {
    const first = p.firstName?.trim()  || '';
    const last  = p.lastName?.trim()   || '';

    // both names present → first letter of each
    if (first && last) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }

    // only first name → first two letters
    if (first) {
      return first.slice(0, 2).toUpperCase();
    }

    // only last name → first two letters
    if (last) {
      return last.slice(0, 2).toUpperCase();
    }

    // no name given
    return '';
  }
}
